import { Spinner } from '../components/feedback';
import { OperationBoundary } from '../components/OperationBoundary';
import { EditBoundary, useEditDirty } from '../components/EditBoundary';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { JvmSettingsSchema, type LaunchView } from '@mcpanel/core';
import { api } from '../api/client';
import type { ChangeRequest, ServerView } from '../api/types';
import { errorMessage, useApp } from '../app-context';
import { JvmSettingsFields } from '../components/JvmSettingsFields';
import { ErrorAlert } from '../components/ui';
import { usePoll } from '../hooks/usePoll';
import { ReviewDialog } from './ServerManage';
function Editor({ server, current }: { server: ServerView; current: LaunchView }) {
  const { canOperate, notify } = useApp();
  const [value, setValue] = useState(current.jvm);
  const [request, setRequest] = useState<ChangeRequest>();
  const [busy, setBusy] = useState(false);
  useEditDirty(JSON.stringify(value) !== JSON.stringify(current.jvm));
  async function plan() {
    setBusy(true);
    try {
      setRequest(await api.createRequest('change_jvm_args', JvmSettingsSchema.parse(value), server.id));
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  }
  async function copy() {
    try {
      await navigator.clipboard.writeText(current.command);
      notify('起動コマンドをコピーしました');
    } catch (error) {
      notify(errorMessage(error), true);
    }
  }
  return (
    <section id="java-launch-args" className="card card-body stack">
      <h2>Java の起動引数</h2>
      <p>変更すると Minecraft を再起動します。起動に失敗したら元の引数に戻します。</p>
      <div className="field">
        <label htmlFor="launch-command">実際の起動コマンド</label>
        {/* 読みやすさのため 1 行に 1 引数で、引用符なしで並べる。コピーされるのは 1 行のコマンド（current.command）。 */}
        <pre id="launch-command" className="launch-command" aria-label="実際の起動コマンド">
          {[current.javaExecutable, ...current.args].join('\n')}
        </pre>
      </div>
      <button className="btn" onClick={() => void copy()}>
        コピー
      </button>
      <p>
        Java: {current.javaExecutable}
        {current.javaMajor ? '（Java ' + current.javaMajor + '）' : ''}
      </p>
      <p className="launch-memory">
        メモリ: <code>{current.memoryArgs.join(' ')}</code> — メモリの設定で変更
      </p>
      <JvmSettingsFields value={value} onChange={setValue} disabled={!canOperate || busy} />
      {canOperate && (
        <button
          className="btn primary"
          disabled={
            busy || !JvmSettingsSchema.safeParse(value).success || JSON.stringify(value) === JSON.stringify(current.jvm)
          }
          onClick={() => void plan()}
        >
          {busy && <Spinner />}
          起動引数の変更内容を確認
        </button>
      )}
      {request && <ReviewDialog request={request} onClose={() => setRequest(undefined)} />}
    </section>
  );
}
function LaunchSettingsTabContent({ server }: { server: ServerView }) {
  const [params] = useSearchParams();
  const result = usePoll(() => api.launch(server.id), 10000, [server.id, server.revision]);
  useEffect(() => {
    if (params.get('tab') === 'launch') document.getElementById('java-launch-args')?.scrollIntoView?.();
  }, [params, result.data?.command]);
  return (
    <div className="stack">
      <ErrorAlert error={result.error} />
      {result.data && <Editor server={server} current={result.data} />}
    </div>
  );
}

export function LaunchSettingsTab({ server }: { server: ServerView }) {
  return (
    <OperationBoundary serverId={server.id}>
      <EditBoundary resource={{ target: `server:${server.id}`, screen: 'jvm' }} revision={String(server.revision)}>
        <LaunchSettingsTabContent server={server} />
      </EditBoundary>
    </OperationBoundary>
  );
}
